import { useState, useCallback, useEffect, useRef } from "react";
import type { TreeFolder } from "../types";

/**
 * Hook for managing inline folder rename state and logic
 *
 * Handles the state and logic for inline editing of folder names, including:
 * - Tracking which folder is being edited
 * - Managing the edit text value
 * - Keyboard handlers (Enter to confirm, Escape to cancel)
 * - Auto-focus on input when editing starts
 *
 * @param onRename - Callback function to execute when rename is confirmed
 * @returns Object with rename state and control functions
 *
 * @example
 * ```tsx
 * const {
 *   editingFolderId,
 *   editName,
 *   startRename,
 *   confirmRename,
 *   cancelRename,
 *   handleKeyDown,
 *   setEditName
 * } = useFolderRename((folderId, newName) => {
 *   console.log(`Rename ${folderId} to ${newName}`);
 * });
 *
 * return (
 *   <div>
 *     {editingFolderId === folder.id ? (
 *       <input
 *         value={editName}
 *         onChange={(e) => setEditName(e.target.value)}
 *         onKeyDown={handleKeyDown}
 *         onBlur={() => confirmRename(folder.id)}
 *       />
 *     ) : (
 *       <span onClick={() => startRename(folder)}>{folder.name}</span>
 *     )}
 *   </div>
 * );
 * ```
 */
export function useFolderRename(onRename: (folderId: string, newName: string) => void) {
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const originalName = useRef<string>("");

  const startRename = useCallback((folder: TreeFolder) => {
    setEditingFolderId(folder.id);
    setEditName(folder.name);
    originalName.current = folder.name;
  }, []);

  const confirmRename = useCallback(
    (folderId: string) => {
      const trimmedName = editName.trim();

      // Only rename if name changed and is not empty
      if (trimmedName && trimmedName !== originalName.current) {
        onRename(folderId, trimmedName);
      }

      // Clear editing state
      setEditingFolderId(null);
      setEditName("");
      originalName.current = "";
    },
    [editName, onRename]
  );

  const cancelRename = useCallback(() => {
    setEditingFolderId(null);
    setEditName("");
    originalName.current = "";
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, folderId: string) => {
      if (e.key === "Enter") {
        e.preventDefault();
        confirmRename(folderId);
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancelRename();
      }
    },
    [confirmRename, cancelRename]
  );

  const isEditing = useCallback(
    (folderId: string) => editingFolderId === folderId,
    [editingFolderId]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setEditingFolderId(null);
      setEditName("");
      originalName.current = "";
    };
  }, []);

  return {
    editingFolderId,
    editName,
    startRename,
    confirmRename,
    cancelRename,
    handleKeyDown,
    setEditName,
    isEditing,
  };
}
